<template>
  <SizeConsolidatedVariant v-if="isConsolidatedThirdVariantFlag" :id :product :product-pricing-state />
  <SizeSplitVariant v-else-if="isSplitThirdVariantFlag || memberFlags?.isSplitThirdVariantFlag" :id :product :product-pricing-state />
  <SizeSelection
    v-else
    v-model="selectedSizeId"
    v-model:touched="touched"
    v-model:latchkey="selectedLatchkey"
    v-bind="sizeProps"
    :product-id="productId"
    :base-product-id="selectedId"
    :name="`cta-sizes-${uniqueId}-${selectedId}`"
    :is-size-dropdown="isSizeDropdown"
    :is-size-chart
    :size-chart-img="sizeChartImage"
    :size-chart-id="sizeChartId"
    :brand-name="brandName"
    :type-name="typeName"
    :is-size-chart-align-left="true"
    :class="{
      'is-price-per-oz': shouldShowPricePerOz,
    }"
    :is-price-per-oz="shouldShowPricePerOz"
    :error-text-active
    class="margin-bottom-s"
    @update:model-value="onSizeChange"
  >
    <template #after-chips="{ sizeChips }">
      <TrueFit v-if="isTrueFitAvailable" v-model:touched="touched" v-model:selected-size-id="selectedSizeId" :size-chips="sizeChips" />
    </template>
  </SizeSelection>
</template>

<script setup>
import { ref, computed, useId } from 'vue';
import { storeToRefs } from 'pinia';
import useAvailabilityStore from '~/stores/useAvailabilityStore';
import { useUpc } from '@nuxt-common/media-card-large/app/composables/useUpc';
import { SizeSelection } from '@nuxt-common/media-card-large/app/modules/size';
import { useTraitAnalyticsData } from '@nuxt-common/media-card-large/app/composables/useTraitAnalyticsData';
import TrueFit from '~/modules/truefit/components/TrueFit';
import { useBrand } from '~/composables/useBrand';
import useSizeDropdown from '~/composables/useSizeDropdown';
import { useProductType } from '~/composables/useProductType';
import { useCommonProductGetters } from '~/composables/useCommonProductGetters';
import useExperiments from '~/stores/useExperimentsStore';
import useSelectedLatchkey from '~/modules/latchkey/composables/useSelectedLatchkey';
import useSize from './composables/useSize';
import useFeatureEligibility from '~/modules/featureControl/composables/useFeatureEligibility';
import { useProductFlags } from '~/modules/featureControl/composables/useProductFlags';
import { useProtectionPlanAnalytics } from '~/modules/worry-no-more/analytics/useWnmAnalytics';
import useSizeChart from '~/composables/useSizeChart';
import SizeConsolidatedVariant from '~/modules/third-variant/components/SizeConsolidatedVariant';
import SizeSplitVariant from '~/modules/third-variant/components/SizeSplitVariant';
import { useProductPrice } from '~/modules/price/composables/useProductPrice';
import { useProduct } from '~/modules/product/composables/useProduct';
import useSelectedMemberId from '~/modules/latchkey/composables/useSelectedMemberId';
import useConsolidatedMemberStore from '~/stores/useConsolidatedMemberStore';
import useStickyAtbAnalytics from '~/modules/sticky-product-traits/composables/useStickyAtbAnalytics';
import { useTraitsErrorActive } from '~/modules/sticky-product-traits/composables/context-providers/useTraitsErrorActive';

const { productData: product, productId: id } = useProduct();
const { isMcom, isBcom } = useBrand();
const { showDropdown } = useSizeDropdown();
const { brandName, typeName } = useCommonProductGetters();
const { isTrueFitEligible, isInternationalMode, isShopTheSetExpEnabled } = useFeatureEligibility();
const consolidatedStore = useConsolidatedMemberStore();
const { memberFlags, memberFeatureEligible } = storeToRefs(consolidatedStore);
const selectedLatchkey = useSelectedLatchkey();
const { sizeProps, selectedSizeId, sizes } = useSize();
const { selectedUpcId } = useUpc(id);
const { isSplitThirdVariantFlag, isConsolidatedThirdVariantFlag, beautyFlag } = useProductFlags();
const { getExtraData } = useProtectionPlanAnalytics();
const { extraTraitsData } = useStickyAtbAnalytics();
const { errorTextActive } = useTraitsErrorActive();

const selectedMemberId = useSelectedMemberId();
const selectedId = computed(() => (isShopTheSetExpEnabled ? selectedMemberId.value : id));

const uniqueId = useId();

const {
  experimentFlags: { isPricePerOzTreatmentEnabled },
} = useExperiments();
const availabilityData = computed(() => useAvailabilityStore().data);

// TODO: should be exposed from ProductPrice level
// and then passed to onSizeChange on ProductTraits level
const selectedPrice = useProductPrice();
const productPricingState = computed(() => [String(selectedPrice.value?.priceTypeId || 'ZERO')]);

const { isConsolidatedMaster } = useProductType();
const isSizeDropdown = computed(
  () => isBcom && (sizeProps.value.isSizeDropdown || (!isShopTheSetExpEnabled && showDropdown.value && sizeProps.value.sizes.length > 1)),
);

const touched = ref(false);

const isTrueFitAvailable = computed(() => (memberFeatureEligible.value?.isTrueFitEligible || isTrueFitEligible) && sizes.value.length > 1);
const { sizeChartImage, isSizeChart, productId, sizeChartId } = useSizeChart();

const shouldShowPricePerOz = computed(() => isPricePerOzTreatmentEnabled && beautyFlag && !isInternationalMode);

const { fireTagOnChange } = useTraitAnalyticsData({
  isConsolidatedMaster,
  isMcom,
  product,
  isSplitThirdVariant: isSplitThirdVariantFlag,
  isConsolidatedThirdVariant: isConsolidatedThirdVariantFlag,
});

// We use @update:modelValue so it only listens to when user click on size
// TOD: move this to ProductTraits scope
function onSizeChange() {
  setTimeout(() => {
    fireTagOnChange({
      selectedUpcId: selectedUpcId.value,
      productPricingState: productPricingState.value,
      selectedSddData: availabilityData.value?.upcNumberSddMap || {},
      extraData: {
        ...getExtraData(),
        ...extraTraitsData('size'),
      },
    });
  }, 0);
}
</script>

<style lang="scss" scoped>
.is-price-per-oz {
  :deep(.selection-tile) {
    // TODO: Overrides to reduce cramped spacing, Remove these once Platform team implements the fix on their side.
    padding: 6px 8px !important;
    height: 50px !important;
  }
}
</style>
